import CueTimingDuration from './CueTimingDuration.jsx'
import CueTimingSchaffold from './CueTimingSchaffold.jsx'
import CueTimingStartTime from './CueTimingStartTime.jsx'
import PropTypes from 'prop-types'
import { useState, useCallback } from 'react'
import { useAtomValue, useSetAtom } from 'jotai'
import { getTimestampByCueIdAtom } from '../../../store/timestamps.store.js'
import {
  firstCueIdAtom,
  getNextCueAtom,
  rundownAtom,
  setCueAtom,
  getCueAtom,
} from '../../../store/rundown.store'
import { CueRunState, fastDeepEqual } from '@rundown-studio/utils'
import _intersection from 'lodash/intersection'
import _includes from 'lodash/includes'
import { CueStartMode, RundownPermission } from '@rundown-studio/types'
import { updateRundownCue } from '../../../firestore.js'
import { applyDate } from '@rundown-studio/timeutils'
import { differenceInCalendarDays } from 'date-fns'
import { tz } from '@date-fns/tz'
import useDeepCompareMemo from '../../../utils/useDeepCompareMemo.js'
import { runnerStateAtom } from '../../../store/runner.store.js'
import { useRundownPermission } from '../../../features/permissions/lib/use-rundown-permission.ts'

/**
 * CueTiming Component
 *
 * Displays and manages time-related information for a cue, including:
 * - Start time (with scheduling options)
 * - Duration
 * - Time variance indicators
 *
 * The component handles both display and editing of timing information,
 * synchronizing with Firestore for persistence.
 */
export default function CueTiming({ rundownId, cueId, timezone, cellStyle, skipDuringShow }) {
  // Atoms and derived state
  const runnerState = useAtomValue(runnerStateAtom)
  const setCue = useSetAtom(setCueAtom)
  const rundown = useAtomValue(rundownAtom)
  const getTimestampByCueId = useAtomValue(getTimestampByCueIdAtom)
  const getCue = useAtomValue(getCueAtom)
  const cue = getCue(cueId)
  const timestamp = getTimestampByCueId(cueId)
  const getNextCue = useAtomValue(getNextCueAtom)
  const nextCue = getNextCue(cueId)
  const firstCueId = useAtomValue(firstCueIdAtom)
  const isFirstCue = cueId === firstCueId

  const isSoftCue = cue.startMode === CueStartMode.FLEXIBLE

  // UI state
  const [loading, setLoading] = useState([])

  const isEditor = useRundownPermission({
    permission: RundownPermission.EDIT,
  })
  // Access and editing state
  const readonly =
    cue.settings?.preventEdits ||
    timestamp?.state === CueRunState.CUE_PAST ||
    !isEditor

  // Extract only the needed timestamp properties to avoid unnecessary recalculations
  const timestampOriginalStart = timestamp?.original?.start
  const timestampActualStart = timestamp?.actual?.start

  /**
   * Calculate effective values that will be passed down.
   * This includes any derived or computed values.
   * - First cue startTime comes from rundown and is always FIXED
   *
   * Using useDeepCompareMemo for both preflight (dependency) and postflight (result) comparison
   * to avoid unnecessary reference changes when data is deeply equal.
   */
  const effectiveValues = useDeepCompareMemo(() => {
    const originalStart = timestampOriginalStart || new Date(cue.startTime)
    const actualStart = timestampActualStart || new Date(cue.startTime)

    // Start Time
    // Soft cues have no persisted startTime — their timing is computed from
    // preceding cues and lives in the `timestamp` prop. Keeping null here
    // ensures handleUpdate's change detection sees a real diff when switching
    // a soft cue to FIXED mode (CueTimingStartTime.onPropChange sets the value).
    let startTime = cue.startTime
      ? applyDate(new Date(cue.startTime), originalStart, { timezone })
      : originalStart
    // Use the cue's own startTime (with the rundown date) for the first cue,
    // not rundown.startTime directly — they can be out of sync when the user
    // changes the date and time separately, causing the "time is in the past"
    // check to evaluate against a stale rundown.startTime.
    if (isFirstCue) {
      startTime = cue.startTime
        ? applyDate(new Date(cue.startTime), new Date(rundown.startTime), { timezone })
        : new Date(rundown.startTime)
    }
    if (isSoftCue) startTime = null

    // Start Mode
    let startMode = cue.startMode
    if (isFirstCue) startMode = CueStartMode.FIXED

    // Start Date Plus
    let startDatePlus = cue.startDatePlus
    if (isFirstCue) startDatePlus = 0
    if (isSoftCue)
      startDatePlus = differenceInCalendarDays(actualStart, rundown.startTime, {
        in: tz(timezone),
      })

    return {
      startTime,
      startMode,
      startDatePlus,
      scheduled: cue.scheduled,
      duration: cue.duration,
    }
  }, [
    isFirstCue,
    isSoftCue,
    rundown.startTime,
    cue.startTime,
    cue.startMode,
    cue.startDatePlus,
    cue.scheduled,
    cue.duration,
    timestampOriginalStart,
    timestampActualStart,
    timezone,
  ])

  /**
   * Updates the cue and (for first cue) the rundown
   * - Determine which properties are being updated
   * - Bail early: Skip API call if no actual changes
   * - Add all changed props to loading state
   * - Special handling for first cue
   */
  const handleUpdate = useCallback(
    async (update) => {
      // Determine which properties actually changed
      const changes = {}
      Object.entries(update).forEach(([key, value]) => {
        if (!fastDeepEqual(value, effectiveValues[key])) {
          changes[key] = value
        }
      })

      // Bail if no actual changes
      if (Object.keys(changes).length === 0) return

      // Track loading state for changed properties
      setLoading(Object.keys(changes))
      try {
        const { data } = await updateRundownCue(
          rundownId,
          cue.id,
          changes,
          isFirstCue,
        )
        setCue(data)
      } catch (error) {
        console.error('Failed to update cue timing:', error)
        // Error handling is done by updateRundownCue (shows toast)
      } finally {
        setLoading([])
      }
    },
    [effectiveValues, rundownId, cue.id, isFirstCue, setCue],
  )

  return (
    <>
      {/* Start Time */}
      {skipDuringShow ? (
        <CueTimingSchaffold style={cellStyle}>
          <div className="min-h-max max-h-full h-6 px-1.5 w-full rounded-sm bg-black/50 flex items-center justify-center">
            <span className="opacity-50 tabular-nums">--:--</span>
          </div>
        </CueTimingSchaffold>
      ) : (
        <CueTimingStartTime
        cueId={cue.id}
        values={effectiveValues}
        setValues={handleUpdate}
        readonly={readonly}
        loading={
          _intersection(loading, [
            'startTime',
            'startDatePlus',
            'startMode',
            'scheduled',
          ]).length > 0
        }
        timestamp={timestamp}
        timezone={timezone}
        rundown={rundown}
        isFirstCue={isFirstCue}
        cellStyle={cellStyle}
        timingProps={{
          todDisplayFormat: rundown?.settings?.todDisplayFormat,
          runnerState: runnerState,
          rundownStartTime: new Date(rundown.startTime),
        }}
        nextCue={nextCue}
      />
      )}

      {/* Duration */}
      <CueTimingDuration
        values={effectiveValues}
        setValues={handleUpdate}
        readonly={readonly}
        loading={_includes(loading, 'duration')}
        timestamp={timestamp}
        timezone={timezone}
        cellStyle={cellStyle}
      />
    </>
  )
}

CueTiming.propTypes = {
  rundownId: PropTypes.string.isRequired,
  cueId: PropTypes.string.isRequired,
  timezone: PropTypes.string,
  cellStyle: PropTypes.object,
}
